<template>
  <MarketplaceCard
    v-if="ghl"
    v-model="switchOn"
    :logo-path="GoHighLevelLogo"
    logo-alt="GoHighLevel"
    description="Sync your Sera customers, jobs, memberships and appointments to GoHighLevel — and auto-create a sales pipeline."
    learn-more-link="https://www.gohighlevel.com/"
    cy-selector="marketplaceGHL"
    :notice-text="duplicateNotice"
  >
    <template #configure>
      <div data-cy="setup-information-link" class="link" @click="openModal">
        Set Up Information
      </div>
    </template>
    <template #modal>
      <Modal
        v-if="modalOpen"
        title="Set Up Information - GoHighLevel"
        submit-btn-text="Save"
        delete-btn-text="Disconnect"
        :saving="saving"
        @submit="saveIntegration"
        @delete="confirmDelete"
        @hide-modal="closeModal"
      >
        <div class="d-flex flex-column gap-4">
          <div
            data-cy="ghl-connect-info"
            class="rounded-lg border border-blue-100 bg-blue-50 p-3 text-xs text-gray-700"
          >
            <p class="mb-1 font-medium text-gray-900">What connecting does</p>
            <ul class="ml-4 list-disc space-y-1">
              <li>
                Creates a <strong>“Sera” opportunity pipeline</strong> with
                three stages — <strong>Open → Won → Lost</strong>.
              </li>
              <li>
                Sets up the GoHighLevel objects and links Sera needs (Service
                Addresses, Memberships, Appointments).
              </li>
              <li>
                Your customers, jobs, memberships, appointments and their
                service addresses then sync to GoHighLevel automatically.
              </li>
            </ul>
          </div>
          <InputField
            v-model="apiKey"
            data-cy="ghl-api-key"
            title="Private Integration Token"
            type="password"
            toggleable
            dense
            :placeholder="
              ghl.api_key_present
                ? 'Token saved — enter a new one to replace'
                : 'Enter token'
            "
          />
          <InputField
            v-model="locationId"
            data-cy="ghl-location-id"
            title="Location ID"
            placeholder="Enter GHL location id"
            dense
          />

          <template v-if="ghl.api_key_present">
            <hr class="border-gray-200" />

            <div class="rounded-lg border border-gray-200 bg-gray-50 p-3">
              <div class="d-flex align-items-center gap-2">
                <span
                  :class="
                    needsReauth ? 'ghl-status-dot--warn' : 'ghl-status-dot'
                  "
                />
                <span class="font-medium text-gray-900">
                  {{ needsReauth ? "Reconnect needed" : "Connected" }}
                </span>
                <span class="text-xs text-gray-500">{{ ghl.location_id }}</span>
              </div>
              <p v-if="needsReauth" class="mt-1 text-xs text-amber-700">
                GoHighLevel rejected recent requests. Enter a new Private
                Integration Token and save to reconnect.
              </p>
              <div class="d-flex mt-2 text-sm">
                <div class="flex-1">
                  <div class="text-xs text-gray-500">Last sync</div>
                  <div class="text-gray-900">{{ lastSyncLabel }}</div>
                </div>
                <div class="flex-1">
                  <div class="text-xs text-gray-500">Outbound queue</div>
                  <div class="text-gray-900">
                    {{ ghl.outbound_queue_depth }}
                  </div>
                </div>
              </div>
            </div>

            <div>
              <span class="block mb-2 text-sm font-medium text-gray-900">
                Recent outbound failures
              </span>
              <p
                v-if="!ghl.recent_outbound_failures.length"
                class="text-sm text-gray-500"
              >
                No recent failures.
              </p>
              <ul
                v-else
                class="rounded-lg border border-gray-200 divide-y divide-gray-100 text-sm"
              >
                <li
                  v-for="(failure, index) in ghl.recent_outbound_failures"
                  :key="index"
                  class="d-flex align-items-center justify-content-between px-3 py-2"
                >
                  <span class="text-gray-600 truncate">
                    {{ formatTimestamp(failure.created_at) }} ·
                    {{ failure.endpoint }}
                  </span>
                  <span
                    class="ml-2 rounded bg-red-100 px-1.5 py-0.5 text-xs font-semibold text-red-700"
                  >
                    {{ failure.response_code }}
                  </span>
                </li>
              </ul>
            </div>

            <div class="rounded-lg border border-gray-200 p-3">
              <div class="d-flex align-items-center gap-2">
                <span :class="duplicateDotClass" />
                <span class="font-medium text-gray-900">
                  Multiple opportunities per contact
                </span>
                <span class="text-xs" :class="duplicateTextClass">
                  {{ duplicateStatusLabel }}
                </span>
              </div>
              <template v-if="duplicateOpportunitiesState !== 'enabled'">
                <p class="mt-1 text-xs text-gray-600">
                  By default GoHighLevel allows only one opportunity per
                  contact, so a repeat customer's later jobs won't sync. To give
                  each job its own opportunity, turn this on:
                </p>
                <ol class="mt-1 ml-4 list-decimal text-xs text-gray-600">
                  <li>
                    In GoHighLevel, open
                    <strong>Settings → Objects → Opportunities</strong>.
                  </li>
                  <li>
                    Enable
                    <strong
                      >"Allow more than one opportunity per contact"</strong
                    >
                    and save.
                  </li>
                  <li>Return here and re-check:</li>
                </ol>
                <button
                  type="button"
                  data-cy="ghl-dup-recheck"
                  class="inline-flex items-center gap-2 mt-3 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 !rounded-lg hover:bg-gray-100 disabled:opacity-60 disabled:cursor-not-allowed"
                  :disabled="duplicateChecking"
                  title="Re-read the setting from GoHighLevel"
                  @click="refreshDuplicateSetting"
                >
                  <i
                    class="fa-regular fa-arrows-rotate"
                    :class="{ 'fa-spin': duplicateChecking }"
                  />
                  {{ duplicateChecking ? "Re-checking…" : "Re-check" }}
                </button>
              </template>
            </div>
          </template>
        </div>
      </Modal>
      <Modal
        v-if="deleteConfirm"
        title="Disconnect GoHighLevel"
        @hide-modal="toggleDeleteConfirm"
      >
        <div>
          This disconnects GoHighLevel and stops syncing your records —
          customers, jobs, memberships, appointments and their service
          addresses. Are you sure?
        </div>
        <template #submit>
          <Button id="submit_modal_btn" type="danger" @click="dropIntegration">
            Disconnect
          </Button>
        </template>
      </Modal>
    </template>
  </MarketplaceCard>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";

import Button from "@/components/Button.vue";
import Modal from "@/components/Modal.vue";
import InputField from "@/components/Seranova/InputField/InputField.vue";
import GoHighLevelLogo from "@/images/gohighlevel_logo.png";
import MarketplaceCard from "./MarketplaceCard.vue";

const store = useStore();

const switchOn = ref<boolean | null>(null);
const modalOpen = ref(false);
const deleteConfirm = ref(false);
const saving = ref(false);
const apiKey = ref("");
const locationId = ref("");

const ghl = computed(() => store.state.ghl);

// GoHighLevel rejects a rotated/revoked token with 401/403. Rather than keep
// showing a reassuring green "Connected", surface a reconnect prompt when the
// most recent outbound attempt failed for auth — but only when that failure is
// newer than the last time the credentials were known good. Saving re-verifies
// the token (bumping updated_at) and a successful sync bumps last_sync_at, so
// either clears a stale warning without waiting for the failures to age out.
const needsReauth = computed(() => {
  if (!ghl.value?.api_key_present) return false;
  const newest = ghl.value?.recent_outbound_failures?.[0];
  if (!newest || ![401, 403].includes(newest.response_code)) return false;
  const knownGoodAt = Math.max(
    Date.parse(ghl.value?.updated_at ?? "") || 0,
    Date.parse(ghl.value?.last_sync_at ?? "") || 0
  );
  return (Date.parse(newest.created_at) || 0) > knownGoodAt;
});

const lastSyncLabel = computed(() =>
  ghl.value?.last_sync_at ? formatTimestamp(ghl.value.last_sync_at) : "Never"
);

// GHL's "Allow more than one opportunity per contact" location setting. It is read
// on demand (modal open / re-check) via a dedicated endpoint, NOT as part of the
// health payload — so a Marketplace page load never blocks on a GHL round-trip.
// undefined = not read yet, null = read failed / not connected → both "unknown".
const duplicateOpportunitiesAllowed = ref<boolean | null | undefined>(
  undefined
);
const duplicateChecking = ref(false);

const duplicateOpportunitiesState = computed(() => {
  const allowed = duplicateOpportunitiesAllowed.value;
  if (allowed === true) return "enabled";
  if (allowed === false) return "disabled";
  return "unknown";
});

const duplicateStatusLabel = computed(
  () =>
    ({ enabled: "Enabled", disabled: "Not enabled", unknown: "Not checked" })[
      duplicateOpportunitiesState.value
    ]
);

const duplicateDotClass = computed(() =>
  duplicateOpportunitiesState.value === "enabled"
    ? "ghl-status-dot"
    : "ghl-status-dot--warn"
);

const duplicateTextClass = computed(() =>
  duplicateOpportunitiesState.value === "enabled"
    ? "text-gray-500"
    : "text-amber-700"
);

// Card-face warning, shown only when GHL is connected and the setting is
// confirmed off. An unknown/unread state stays silent rather than nagging.
const duplicateNotice = computed(() =>
  ghl.value?.api_key_present && duplicateOpportunitiesState.value === "disabled"
    ? "Multiple opportunities off"
    : ""
);

function refreshDuplicateSetting() {
  if (!ghl.value?.api_key_present) {
    duplicateOpportunitiesAllowed.value = undefined;
    return Promise.resolve();
  }
  duplicateChecking.value = true;
  return store
    .dispatch("checkGhlDuplicateOpportunities")
    .then((allowed) => (duplicateOpportunitiesAllowed.value = allowed))
    .catch(() => (duplicateOpportunitiesAllowed.value = null))
    .finally(() => (duplicateChecking.value = false));
}

function formatTimestamp(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString();
}

watch(switchOn, (newValue, oldValue) => {
  if (
    oldValue === null ||
    newValue === oldValue ||
    newValue === ghl.value?.enabled
  ) {
    return;
  }
  if (newValue) {
    enableIntegration();
  } else {
    disableIntegration();
  }
});

watch(
  () => ghl.value?.enabled,
  (newValue, oldValue) => {
    if (oldValue !== newValue) refreshTools();
  }
);

onMounted(() => {
  store.dispatch("fetchGhl").then(() => {
    refreshTools();
    // Only connected tenants read the setting; refreshDuplicateSetting no-ops
    // otherwise, so a non-connected card makes no GoHighLevel round-trip.
    refreshDuplicateSetting();
  });
});

function openModal() {
  store.dispatch("fetchGhl").then(() => {
    apiKey.value = "";
    locationId.value = ghl.value?.location_id ?? "";
    modalOpen.value = true;
    refreshDuplicateSetting();
  });
}

function closeModal() {
  return store.dispatch("fetchGhl").then(() => {
    modalOpen.value = false;
    refreshTools();
  });
}

function enableIntegration() {
  // Enabling needs a stored token; if none, bounce back and open the form.
  if (!ghl.value?.api_key_present) {
    switchOn.value = false;
    window.notify.error(
      "Add a Private Integration Token before enabling GoHighLevel."
    );
    openModal();
    return;
  }
  store
    .dispatch("changeGhl", { enabled: true })
    .then(() => window.notify.success("GoHighLevel integration enabled!"))
    .catch(() => (switchOn.value = false));
}

function disableIntegration() {
  // Nothing is stored (e.g. just disconnected) — there is nothing to disable,
  // and posting enabled:false with no token trips the api_key validation.
  if (!ghl.value?.api_key_present) return;
  store
    .dispatch("changeGhl", { enabled: false })
    .then(() => window.notify.success("GoHighLevel integration disabled!"));
}

async function saveIntegration() {
  if (!ghl.value?.api_key_present && !apiKey.value) {
    window.notify.error("A Private Integration Token is required.");
    return;
  }
  try {
    saving.value = true;
    const payload: Record<string, unknown> = {
      location_id: locationId.value,
      enabled: true
    };
    if (apiKey.value) payload.api_key = apiKey.value;
    await store.dispatch("changeGhl", payload);
    window.notify.success("GoHighLevel settings saved!");
    closeModal();
  } catch (e: any) {
    // The backend verifies the token/location against GHL and rejects bad ones.
    window.notify.error(
      e?.response?.data?.message ||
        "Could not connect to GoHighLevel. Check the token and location id."
    );
  }
  saving.value = false;
}

function dropIntegration() {
  deleteConfirm.value = false;
  // Drop first, then sync the toggle from the fresh (disabled) config. Setting
  // switchOn manually before the drop would fire the enabled-watch and post a
  // stray change against the now-empty config.
  store.dispatch("dropGhl").then(() => {
    modalOpen.value = false;
    refreshTools();
    window.notify.success("GoHighLevel disconnected.");
  });
}

function refreshTools() {
  switchOn.value = ghl.value?.enabled ?? false;
}

function confirmDelete() {
  deleteConfirm.value = true;
}

function toggleDeleteConfirm() {
  deleteConfirm.value = !deleteConfirm.value;
}

defineExpose({ openModal });
</script>

<style lang="scss" scoped>
.ghl-status-dot,
.ghl-status-dot--warn {
  width: 8px;
  height: 8px;
  border-radius: 9999px;
  display: inline-block;
}

.ghl-status-dot {
  background-color: #22c55e;
}

.ghl-status-dot--warn {
  background-color: #f59e0b;
}
</style>
